/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router';
import { BaseLayout } from '@link/admin/layouts';
import { Loader } from '@link/share/components';
import { INBOX_ENABLED } from '@link/share/constants';
import {
  NotFound,
  PrePayment,
  PrivacyPolicy,
  LegalNotice
} from '@link/share/views';
import PrivateRoute from './PrivateRoute';

const isProd = import.meta.env.VITE_APP_ADMIN_ENV === 'production';

const Dashboard = lazy(() => import('@link/admin/views/Dashboard'));
const ContractsList = lazy(() => import('@link/admin/views/Contracts/List'));
const ContractDetail = lazy(() => import('@link/admin/views/Contracts/Detail'));
const ContractWhitelist = lazy(
  () => import('@link/admin/views/Contracts/Whitelist')
);
const ActivityPublish = lazy(
  () => import('@link/admin/views/Contracts/ActivityPublish')
);
const SuppliersList = lazy(() => import('@link/admin/views/Suppliers/List'));
const SupplierDetail = lazy(() => import('@link/admin/views/Suppliers/Detail'));
const SuppliersAccount = lazy(
  () => import('@link/admin/views/Suppliers/Account')
);
const SuppliersAccountDetail = lazy(
  () => import('@link/admin/views/Suppliers/AccountDetail')
);
const AgentsList = lazy(() => import('@link/admin/views/Agents/List'));
const NewAgent = lazy(() => import('@link/admin/views/Agents/NewAgent'));
const AgentDetail = lazy(() => import('@link/admin/views/Agents/AgentDetail'));
const OrgList = lazy(() => import('@link/admin/views/Agents/OrgList'));
const OrgDetail = lazy(() => import('@link/admin/views/Agents/OrgDetail'));
const AgentsAccountList = lazy(
  () => import('@link/admin/views/Agents/AccountList')
);
const AccountDetail = lazy(
  () => import('@link/admin/views/Agents/AccountDetail/AccountDetail')
);
const ApiKeyList = lazy(() => import('@link/admin/views/Agents/ApiKeyList'));
const WebHook = lazy(() => import('@link/admin/views/Agents/WebHook'));
const AgentsCharge = lazy(() => import('@link/admin/views/Agents/Charge'));
const FinanceTransactions = lazy(
  () => import('@link/admin/views/Finance/Transactions')
);
const FinanceCharge = lazy(() => import('@link/admin/views/Finance/Charge'));
const FinanceChargeHistory = lazy(
  () => import('@link/admin/views/Finance/ChargeHistory')
);
const FinanceChange = lazy(() => import('@link/admin/views/Finance/Change'));
const DataExport = lazy(() => import('@link/admin/views/DataExport'));
const OpsFreeze = lazy(() => import('@link/admin/views/OpsFreeze'));
const Activities = lazy(() => import('@link/admin/views/Activities'));
const NotificationsList = lazy(
  () => import('@link/admin/views/Notifications/List')
);
const NotificationDetail = lazy(
  () => import('@link/admin/views/Notifications/Detail')
);
const NotificationsInbox = lazy(
  () => import('@link/admin/views/Notifications/Inbox')
);

const Login = lazy(() => import('@link/admin/views/Login'));

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback={<Loader />}>{view}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route index element={<Navigate to="contracts" replace />} />
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route path="contracts">
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list">
              <Route index element={lazyView(<ContractsList />)} />
              <Route
                path=":contractId"
                element={lazyView(<ContractDetail />)}
              />
            </Route>
            <Route path="whitelist" element={lazyView(<ContractWhitelist />)} />
            <Route
              path="activity-publish"
              element={lazyView(<ActivityPublish />)}
            />
            <Route path="new" element={lazyView(<ContractDetail />)} />
            <Route path=":contractId" element={lazyView(<ContractDetail />)} />
          </Route>
          <Route path="suppliers">
            <Route path="list">
              <Route index element={lazyView(<SuppliersList />)} />
              <Route path="new" element={lazyView(<SupplierDetail />)} />
              <Route
                path=":supplierId"
                element={lazyView(<SupplierDetail />)}
              />
            </Route>
            <Route path="accounts">
              <Route index element={lazyView(<SuppliersAccount />)} />
              <Route
                path="new"
                element={lazyView(<SuppliersAccountDetail />)}
              />
              <Route
                path=":supplierId/:accountId"
                element={lazyView(<SuppliersAccountDetail />)}
              />
            </Route>
          </Route>
          <Route path="agents">
            <Route path="list">
              <Route index element={lazyView(<AgentsList />)} />
              <Route path="new" element={lazyView(<NewAgent />)} />
              <Route path=":agentId" element={lazyView(<AgentDetail />)} />
            </Route>
            <Route path="orgs">
              <Route path=":agentId?" element={lazyView(<OrgList />)} />
              <Route path="new" element={lazyView(<OrgDetail />)} />
              <Route path=":agentId/:orgId" element={lazyView(<OrgDetail />)} />
            </Route>
            <Route path="accounts">
              <Route
                path=":agentId?/:orgId?"
                element={lazyView(<AgentsAccountList />)}
              />
              <Route path="new" element={lazyView(<AccountDetail />)} />
              <Route
                path=":agentId/:orgId/:accountId"
                element={lazyView(<AccountDetail />)}
              />
            </Route>
            <Route
              path="api-keys/:agentId?/:orgId?"
              element={lazyView(<ApiKeyList />)}
            />
            <Route path="web-hook/:otaId?" element={lazyView(<WebHook />)} />
            <Route path="charge" element={lazyView(<AgentsCharge />)} />
          </Route>
          <Route path="finance">
            <Route
              path="transactions"
              element={lazyView(<FinanceTransactions />)}
            />
            <Route
              path="transactions/:invoiceNumber"
              element={lazyView(<FinanceChange />)}
            />
            <Route path="charge">
              <Route index element={lazyView(<FinanceCharge />)} />
              <Route
                path=":chargeId"
                element={lazyView(<FinanceChargeHistory />)}
              />
            </Route>
          </Route>
          <Route path="notifications">
            {INBOX_ENABLED && (
              <Route path="inbox" element={lazyView(<NotificationsInbox />)} />
            )}
            <Route path="manual">
              <Route index element={lazyView(<NotificationsList />)} />
              <Route path="new" element={lazyView(<NotificationDetail />)} />
              <Route
                path=":notificationId"
                element={lazyView(<NotificationDetail />)}
              />
            </Route>
          </Route>
          {/* 在庫商品一覧：后端未就绪，本番环境暂不开放 */}
          {!isProd && (
            <Route path="activities" element={lazyView(<Activities />)} />
          )}
          {/* OPS凍結：本番环境暂不开放 */}
          {!isProd && (
            <Route path="ops-freeze" element={lazyView(<OpsFreeze />)} />
          )}
          {/* データ出力：本番环境暂不开放 */}
          {!isProd && (
            <Route path="data-export" element={lazyView(<DataExport />)} />
          )}
        </Route>
      </Route>
      <Route path="/finance/pre-payment" element={lazyView(<PrePayment />)} />
      <Route path="/login" element={lazyView(<Login />)} />
      <Route path="/privacy-policy" element={lazyView(<PrivacyPolicy />)} />
      <Route path="/legal-notice" element={lazyView(<LegalNotice />)} />
      <Route path="*" element={lazyView(<NotFound />)} />
    </Route>
  )
);

export default router;
